import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { MapControls } from '@react-three/drei'
import * as THREE from 'three'
import { useExpedition } from '../store/useExpedition.js'

const OVERVIEW = { position:[8,10.5,-38], target:[-6,2.2,5], fov:38 }
const UP = new THREE.Vector3(0,1,0)
const FLOOR_CLEARANCE = 1.05
const WALK_ACCELERATION = 22
const SPRINT_ACCELERATION = 46
const AIR_ACCELERATION = 16
const LINEAR_DRAG = 7.2

const asArray = value => value?.isVector3 ? value.toArray() : value
const validPoint = value => Array.isArray(value) && value.length >= 3 && value.every(Number.isFinite)

function normalizeDirective(value) {
  if (!value) return null
  const position = asArray(value.position || value.pos || value.camera)
  const target = asArray(value.target || value.lookAt || value.focus)
  if (!validPoint(position) || !validPoint(target)) return null
  return {
    position,
    target,
    fov:Number.isFinite(value.fov) ? value.fov : 38,
    speed:Number.isFinite(value.speed) ? value.speed : 1.35,
    id:value.id ?? value.key ?? value,
  }
}

function nearestRoutePoint(position, points) {
  if (!Array.isArray(points) || !points.length) return null
  let nearest = null
  let distanceSq = Infinity
  for (const raw of points) {
    if (!validPoint(raw)) continue
    const dx = raw[0]-position.x
    const dz = raw[2]-position.z
    const nextDistance = dx*dx+dz*dz
    if (nextDistance < distanceSq) { nearest=raw; distanceSq=nextDistance }
  }
  return nearest ? { point:nearest, distance:Math.sqrt(distanceSq) } : null
}

function collectTerrain(scene, destination) {
  destination.length = 0
  scene.traverse(object => {
    if (object.isMesh && (object.userData.terrainSurface || object.name === 'BoulderTerrainLocal')) {
      object.updateWorldMatrix(true,false)
      destination.push(object)
    }
  })
}

/**
 * V6 camera system.
 *
 * Optional store integration (all graceful when absent):
 * cameraDirective/documentaryTarget: { id, position:[x,y,z], target:[x,y,z], fov?, speed? }
 * routeMagnetism: false | true | 0..1
 * returnToRopeRequest: a changing token (number/string) or { id }
 * completeCameraTransition(id): notified after a return-to-rope transition
 * photoMode: keeps controls enabled but suppresses authored camera takeover
 */
export function CameraRig({ reducedMotion=false }) {
  const controls = useRef(null)
  const { camera, scene, gl } = useThree()
  const state = useExpedition()
  const {
    mode='overview', activeEssay=null, activeTheme=null, routeProjections={}, ropeProjection=[],
    freeExplore=false, guided=false, cameraDirective=null, documentaryTarget=null,
    routeMagnetism=false, returnToRopeRequest=null, photoMode=false, fieldMode='fly',
  } = state

  const terrain = useRef([])
  const keys = useRef(new Set())
  const velocity = useRef(new THREE.Vector3())
  const userOrbit = useRef(false)
  const transition = useRef(null)
  const previousReturnRequest = useRef(returnToRopeRequest)
  const floorRay = useMemo(() => new THREE.Raycaster(), [])
  const targetRayOrigin = useMemo(() => new THREE.Vector3(), [])
  const desired = useRef(OVERVIEW)

  const authoredDirective = normalizeDirective(cameraDirective || documentaryTarget)
  const activeRoute = activeTheme && routeProjections?.[activeTheme]?.length
    ? routeProjections[activeTheme]
    : ropeProjection
  // Entered V6 worlds are roamable unless a story beat has explicitly taken the camera.
  const hasAuthoredFocus = Boolean(authoredDirective || guided || activeEssay || activeTheme)
  const roaming = Boolean(freeExplore || (!hasAuthoredFocus && mode !== 'entry'))

  useEffect(() => {
    collectTerrain(scene,terrain.current)
  }, [scene])

  useEffect(() => {
    const editable = target => target instanceof HTMLElement &&
      (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
    const down = event => {
      if (editable(event.target)) return
      if (/^(Key[WASDQE]|ShiftLeft|ShiftRight)$/.test(event.code)) {
        keys.current.add(event.code)
        if (/^Key[WASDQE]$/.test(event.code)) event.preventDefault()
      }
    }
    const up = event => keys.current.delete(event.code)
    const blur = () => { keys.current.clear(); velocity.current.set(0,0,0) }
    window.addEventListener('keydown',down,{passive:false})
    window.addEventListener('keyup',up)
    window.addEventListener('blur',blur)
    return () => {
      window.removeEventListener('keydown',down)
      window.removeEventListener('keyup',up)
      window.removeEventListener('blur',blur)
    }
  }, [])

  useEffect(() => {
    if (authoredDirective && !photoMode) {
      desired.current = authoredDirective
      transition.current = { type:'authored', id:authoredDirective.id }
      userOrbit.current = false
      velocity.current.set(0,0,0)
      return
    }
    if (activeEssay?.position && !photoMode) {
      const [x,y,z] = activeEssay.position
      desired.current = { position:[x+6.8,y+4.6,z+10.5], target:[x,y,z], fov:38, speed:1.4 }
      transition.current = { type:'authored', id:activeEssay.id }
      userOrbit.current = false
      velocity.current.set(0,0,0)
      return
    }
    if (activeTheme && routeProjections?.[activeTheme]?.length && !photoMode) {
      const route = routeProjections[activeTheme]
      const point = route[Math.floor(route.length*.48)]
      desired.current = { position:[point[0]+11,point[1]+11,point[2]+18], target:point, fov:38, speed:1.15 }
      transition.current = { type:'authored', id:activeTheme }
      userOrbit.current = false
      velocity.current.set(0,0,0)
      return
    }
    desired.current = OVERVIEW
    if (roaming || photoMode) transition.current = null
    else transition.current = { type:'authored', id:'overview' }
  }, [activeEssay,activeTheme,authoredDirective?.id,mode,photoMode,roaming,routeProjections])

  useEffect(() => {
    const requestId = returnToRopeRequest?.id ?? returnToRopeRequest
    const previousId = previousReturnRequest.current?.id ?? previousReturnRequest.current
    previousReturnRequest.current = returnToRopeRequest
    if (requestId == null || requestId === previousId) return
    const nearest = nearestRoutePoint(camera.position,activeRoute)
    if (!nearest) return
    const [x,y,z] = nearest.point
    const direction = camera.position.clone().sub(new THREE.Vector3(x,y,z)).setY(0)
    if (direction.lengthSq() < .01) direction.set(0,0,1)
    direction.normalize()
    desired.current = {
      position:[x+direction.x*7,y+4.2,z+direction.z*7], target:[x,y+.25,z], fov:38, speed:1.8,
    }
    transition.current = { type:'rope-return', id:requestId }
    userOrbit.current = false
    velocity.current.set(0,0,0)
  }, [returnToRopeRequest,activeRoute,camera])

  const terrainHeight = (x,z) => {
    if (!terrain.current.length) collectTerrain(scene,terrain.current)
    targetRayOrigin.set(x,120,z)
    floorRay.set(targetRayOrigin,new THREE.Vector3(0,-1,0))
    const hit = floorRay.intersectObjects(terrain.current,false)[0]
    return hit?.point.y
  }

  useFrame((_,rawDelta) => {
    const delta = Math.min(rawDelta,.05)
    const controlsApi = controls.current
    const currentTransition = transition.current

    if (currentTransition && !photoMode) {
      const goal = desired.current
      const speed = reducedMotion ? 100 : (goal.speed || 1.35)
      const alpha = 1-Math.exp(-delta*speed)
      camera.position.lerp(new THREE.Vector3(...goal.position),alpha)
      controlsApi?.target.lerp(new THREE.Vector3(...goal.target),alpha)
      camera.fov = THREE.MathUtils.lerp(camera.fov,goal.fov || 38,alpha)
      camera.updateProjectionMatrix()
      controlsApi?.update()
      const arrived = camera.position.distanceToSquared(new THREE.Vector3(...goal.position)) < .015
      if (arrived) {
        if (currentTransition.type === 'rope-return') {
          useExpedition.getState().completeCameraTransition?.(currentTransition.id)
        }
        transition.current = null
      }
      return
    }

    if (roaming || photoMode) {
      const forward = new THREE.Vector3()
      camera.getWorldDirection(forward)
      forward.y=0
      if (forward.lengthSq() < .0001) forward.set(0,0,-1)
      forward.normalize()
      const right = new THREE.Vector3().crossVectors(forward,UP).normalize()
      const input = new THREE.Vector3()
      if(keys.current.has('KeyW')) input.add(forward)
      if(keys.current.has('KeyS')) input.sub(forward)
      if(keys.current.has('KeyD')) input.add(right)
      if(keys.current.has('KeyA')) input.sub(right)
      if(keys.current.has('KeyE')) input.y+=1
      if(keys.current.has('KeyQ')) input.y-=1
      const pad=typeof navigator!=='undefined'?navigator.getGamepads?.()?.[0]:null
      if(pad){
        const dead=value=>Math.abs(value)>.16?value:0
        input.addScaledVector(forward,-dead(pad.axes[1]||0))
        input.addScaledVector(right,dead(pad.axes[0]||0))
        if(fieldMode!=='hike') input.y+=(pad.buttons[7]?.value||0)-(pad.buttons[6]?.value||0)
      }
      if(input.lengthSq()) {
        input.normalize()
        const sprint = keys.current.has('ShiftLeft') || keys.current.has('ShiftRight')
        const acceleration = fieldMode==='hike' ? 13 : sprint ? SPRINT_ACCELERATION : WALK_ACCELERATION
        input.y *= AIR_ACCELERATION/acceleration
        velocity.current.addScaledVector(input,acceleration*delta)
      }
      velocity.current.multiplyScalar(Math.exp(-LINEAR_DRAG*delta))

      const magnetStrength = routeMagnetism === true ? .16 : Number(routeMagnetism) || 0
      if (magnetStrength > 0 && activeRoute?.length) {
        const nearest = nearestRoutePoint(camera.position,activeRoute)
        if (nearest && nearest.distance < 9) {
          const pull = new THREE.Vector3(nearest.point[0]-camera.position.x,0,nearest.point[2]-camera.position.z)
          velocity.current.addScaledVector(pull,Math.min(.35,magnetStrength)*delta*1.7)
        }
      }

      if (velocity.current.lengthSq() > .000001) {
        const movement = velocity.current.clone().multiplyScalar(delta)
        const next = camera.position.clone().add(movement)
        const floor = terrainHeight(next.x,next.z)
        if (Number.isFinite(floor)) next.y = fieldMode==='hike' ? floor+1.72 : Math.max(next.y,floor+FLOOR_CLEARANCE)
        camera.position.copy(next)
        controlsApi?.target.add(movement)
        if (Number.isFinite(floor) && controlsApi) controlsApi.target.y = Math.max(controlsApi.target.y,floor+.15)
      } else {
        const floor = terrainHeight(camera.position.x,camera.position.z)
        if(fieldMode==='hike'&&Number.isFinite(floor)) camera.position.y=THREE.MathUtils.lerp(camera.position.y,floor+1.72,.22)
        else if (Number.isFinite(floor) && camera.position.y < floor+FLOOR_CLEARANCE) {
          const lift = floor+FLOOR_CLEARANCE-camera.position.y
          camera.position.y += lift
          if (controlsApi) controlsApi.target.y += lift
        }
      }
      controlsApi?.update()
      return
    }

    if (!userOrbit.current) transition.current = { type:'authored', id:'resume' }
    controlsApi?.update()
  })

  return <MapControls
    ref={controls}
    args={[camera,gl.domElement]}
    makeDefault
    enabled
    enableRotate
    enableZoom
    enablePan={roaming || photoMode}
    enableDamping={!reducedMotion}
    dampingFactor={reducedMotion ? 0 : .065}
    panSpeed={.72}
    rotateSpeed={.62}
    zoomSpeed={.8}
    minDistance={2.8}
    maxDistance={95}
    minPolarAngle={.08}
    maxPolarAngle={1.49}
    screenSpacePanning={false}
    onStart={()=>{ userOrbit.current=true; if (roaming || photoMode) transition.current=null }}
    onEnd={()=>{ userOrbit.current=false }}
  />
}
